import { useState, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileSpreadsheet, CheckCircle2, ArrowRight, Users2, FileText, AlertTriangle, CopyCheck, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { runChunkedImport } from "@/lib/chunkedImport";

const FIELDS = {
  anagrafiche: [
    { key: "tipo", label: "Tipo (fisica/giuridica) — opzionale" },
    { key: "cognome", label: "Cognome" },
    { key: "nome", label: "Nome" },
    { key: "ragione_sociale", label: "Denominazione (Ragione Sociale)", aliases: ["denominazione", "ragionesociale"] },
    { key: "indirizzo", label: "Indirizzo" },
    { key: "citta", label: "Citta", aliases: ["citta", "città", "comune"] },
    { key: "provincia", label: "Provincia" },
    { key: "cf_piva", label: "C.FIS/P.IVA (Codice Fiscale o Partita IVA)", aliases: ["cfispiva", "cfis", "cfpiva"] },
    { key: "telefono_fisso", label: "Telefono Fisso" },
    { key: "cellulare", label: "Cellulare" },
    { key: "email", label: "E-Mail", aliases: ["email", "mail"] },
    { key: "data_nascita", label: "Data Nascita" },
    { key: "comune_nascita", label: "Comune Nascita" },
    { key: "provincia_nascita", label: "Provincia Nascita" },
    { key: "occupazione", label: "Occupazione" },
    { key: "professione", label: "Professione" },
    { key: "reddito_mensile", label: "Reddito Mensile", aliases: ["reddiomensile", "reddito"] },
    { key: "azienda_lavoro", label: "Azienda Lavoro" },
    { key: "codice_fiscale", label: "Codice Fiscale (colonna separata) — opzionale", aliases: ["codicefiscale"] },
    { key: "partita_iva", label: "Partita IVA (colonna separata) — opzionale", aliases: ["partitaiva"] },
    { key: "referente", label: "Referente" },
    { key: "telefono", label: "Telefono (generico) — opzionale" },
  ],
  pratiche: [
    { key: "numero", label: "Pratica ID / Numero", aliases: ["id", "praticaid", "codice", "codicepratica", "numeropratica"] },
    { key: "numero_istituto", label: "Codice Istituto (N. pratica istituto)", aliases: ["codiceistituto", "npraticaistituto", "praticaistituto", "nistituto", "npratica"] },
    { key: "istituto_finanziario", label: "Istituto Finanziario", aliases: ["istitutofinanziario", "istituto", "finanziaria", "banca"] },
    { key: "data_caricamento", label: "Data di caricamento", aliases: ["datacaricamento", "data", "datainserimento"] },
    { key: "data_modifica", label: "Data di modifica", aliases: ["datamodifica", "ultimamodifica"] },
    { key: "cliente_nome", label: "Nome Cliente *", aliases: ["nome", "nomecliente", "cliente", "nominativo"] },
    { key: "cliente_cognome", label: "Cognome Cliente", aliases: ["cognome", "cognomecliente"] },
    { key: "azienda", label: "Azienda / Ragione sociale", aliases: ["azienda", "ragionesociale", "denominazione"] },
    { key: "soggetto", label: "Privato o Azienda", aliases: ["privatooazienda", "privatoazienda", "soggetto", "tiposoggetto"] },
    { key: "provincia", label: "Provincia", aliases: ["provincia", "prov"] },
    { key: "email", label: "E-Mail", aliases: ["email", "mail"] },
    { key: "telefono", label: "Telefono", aliases: ["telefono", "tel"] },
    { key: "cellulare", label: "Cellulare", aliases: ["cellulare", "cell", "mobile"] },
    { key: "convenzionato", label: "Rete: Convenzionato", aliases: ["convenzionato", "retevendita", "rete", "partner"] },
    { key: "collaboratore", label: "Rete: Collaboratore", aliases: ["collaboratore", "collaboratoreinterno", "commerciale"] },
    { key: "segnalatore", label: "Rete: Segnalatore", aliases: ["segnalatore", "segnalatori"] },
    { key: "agente", label: "Agente Attuale", aliases: ["agenteattuale", "agente"] },
    { key: "in_carico", label: "In carico a", aliases: ["incaricoa", "incarico", "incaricato"] },
    { key: "canale", label: "Canale", aliases: ["canale", "tipologiacanale", "tipocanale"] },
    { key: "prodotto", label: "Prodotto / Tipologia", aliases: ["prodotto", "tipologia", "tipofinanziamento", "tipo", "schedaprodotto"] },
    { key: "sede", label: "Sede", aliases: ["sede", "filiale"] },
    { key: "importo", label: "Importo" },
    { key: "status", label: "Stato", aliases: ["stato", "statoattuale"] },
    { key: "data_liquidazione", label: "Data di liquidazione", aliases: ["dataliquidazione", "liquidazione", "dataliquidata", "dataerogazione", "erogazione"] },
    { key: "note", label: "Note", aliases: ["note", "nota", "noteiniziale"] },
    { key: "note_interne", label: "Note Interne", aliases: ["noteinterne", "notainterna", "internalnote"] },
    { key: "provv_entrata", label: "Provvigioni in Entrata", aliases: ["provvigioniinentrata", "provvigionientrata", "provvigioneentrata", "provventrata", "compensoentrata", "entrata"] },
    { key: "provv_uscita", label: "Provvigioni in Uscita", aliases: ["provvigioniinuscita", "provvigioniuscita", "provvigioneuscita", "provvuscita", "compensouscita", "uscita"] },
  ],
};

function guess(field, columns) {
  const norm = (s) => s.toLowerCase().replace(/[^a-z]/g, "");
  const fld = typeof field === "string" ? { key: field } : field;
  const cands = [fld.key, (fld.label || "").replace(/\(.*?\)/g, ""), ...(fld.aliases || [])]
    .map(norm).filter(Boolean);
  const cols = columns.map((c) => ({ c, n: norm(c) }));
  for (const cand of cands) { const e = cols.find((x) => x.n === cand); if (e) return e.c; }
  for (const cand of cands) { const e = cols.find((x) => x.n.startsWith(cand) || cand.startsWith(x.n)); if (e) return e.c; }
  for (const cand of cands) { const e = cols.find((x) => x.n.includes(cand)); if (e) return e.c; }
  return "none";
}

export default function ImportData() {
  const [tipo, setTipo] = useState("anagrafiche");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [validation, setValidation] = useState(null);
  const [viewMode, setViewMode] = useState("mapped");
  const [progress, setProgress] = useState(null);

  const shortLabel = (f) => f.label.split("—")[0].split("(")[0].trim();
  const mval = (r, key) => { const col = mapping[key]; return col ? String(r[col] ?? "") : ""; };

  const validate = useCallback(async () => {
    if (!preview) { setValidation(null); return; }
    try {
      const { data } = await api.post("/import/validate", { tipo, mapping, rows: preview.rows });
      setValidation(data);
    } catch (e) { setValidation(null); }
  }, [preview, mapping, tipo]);
  useEffect(() => { const t = setTimeout(validate, 400); return () => clearTimeout(t); }, [validate]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setDone(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      const auto = {};
      FIELDS[tipo].forEach((f) => { const g = guess(f, data.columns); if (g !== "none") auto[f.key] = g; });
      setMapping(auto);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore lettura file"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const commit = async () => {
    setBusy(true); setProgress({ pct: 0, done: 0, total: preview.rows.length });
    try {
      const res = await runChunkedImport({
        rows: preview.rows, chunkSize: 1000,
        postChunk: (chunk, batchId, offset) => api.post("/import/commit", { tipo, mapping, rows: chunk, batch_id: batchId, offset }).then((r) => r.data),
        onProgress: (pct, done, total) => setProgress({ pct, done, total }),
      });
      if (res.failed) {
        toast.error(`Importazione interrotta dopo ${res.processed}/${preview.rows.length} righe (${res.created} creati). Riprova per completare le righe restanti.`);
        return;
      }
      const upd = res.updated || 0;
      setDone({ created: res.created || 0, updated: upd, errors: res.error_count || 0 });
      setPreview(null); setMapping({});
      if (res.error_count) toast.warning(`Import completato: ${res.created} creati, ${upd} aggiornati · ${res.error_count} errori`);
      else toast.success(upd ? `${res.created} creati, ${upd} aggiornati` : `${res.created} record importati`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore importazione"); }
    finally { setBusy(false); setProgress(null); }
  };

  const setMap = (field, col) => setMapping((m) => ({ ...m, [field]: col === "none" ? undefined : col }));

  const flags = {};
  (validation?.results || []).forEach((x) => { flags[x.index] = x; });

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Importa Dati</h1>
          <p className="text-sm text-muted-foreground mt-1">Importa anagrafiche e pratiche dal tuo vecchio gestionale (Excel o CSV)</p>
        </div>
        <Button asChild variant="outline" size="sm" data-testid="registro-import-btn">
          <Link to="/registro-import"><ClipboardList className="h-4 w-4 mr-2" />Registro Import</Link>
        </Button>
      </div>

      <Card className="p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <Label>Cosa vuoi importare?</Label>
            <Select value={tipo} onValueChange={(v) => { setTipo(v); setPreview(null); setDone(null); }}>
              <SelectTrigger data-testid="import-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="anagrafiche">Anagrafiche (clienti / aziende)</SelectItem>
                <SelectItem value="pratiche">Pratiche di finanziamento</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>File (.xlsx o .csv)</Label>
            <Button asChild variant="outline" className="w-full justify-start" disabled={busy}>
              <label className="cursor-pointer" data-testid="import-file-label">
                <Upload className="h-4 w-4 mr-2" /> {busy ? "Lettura…" : "Seleziona file"}
                <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="import-file-input" onChange={onFile} />
              </label>
            </Button>
          </div>
        </div>

        {done !== null && (
          <div className="flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-700 dark:text-emerald-400" data-testid="import-success">
            <CheckCircle2 className="h-5 w-5 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">{done.updated ? `${done.created} record creati, ${done.updated} aggiornati con successo.` : `${done.created} record importati con successo.`}</p>
              {done.errors > 0 && <p className="mt-1 text-amber-600 dark:text-amber-400">{done.errors} righe con errori. <Link to="/registro-import" className="underline font-medium" data-testid="import-errors-link">Vedi Registro Import</Link> per i dettagli.</p>}
            </div>
          </div>
        )}
      </Card>

      {preview && (
        <>
          <Card className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              {tipo === "anagrafiche" ? <Users2 className="h-4 w-4 text-primary" /> : <FileText className="h-4 w-4 text-primary" />}
              <h2 className="font-heading font-semibold">Mappa le colonne — {preview.total} righe rilevate</h2>
            </div>
            <p className="text-xs text-muted-foreground">Associa ogni campo del gestionale a una colonna del tuo file. I campi non mappati verranno lasciati vuoti.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS[tipo].map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className="text-sm w-1/2 truncate" title={f.label}>{f.label}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <Select value={mapping[f.key] || "none"} onValueChange={(v) => setMap(f.key, v)}>
                    <SelectTrigger className="flex-1" data-testid={`import-map-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— nessuna —</SelectItem>
                      {preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </Card>

          {validation && (
            <Card className="p-4" data-testid="import-validation-summary">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-4 w-4" /> {validation.valid} righe valide</span>
                <span className="inline-flex items-center gap-1.5 text-rose-600 dark:text-rose-400"><AlertTriangle className="h-4 w-4" /> {validation.missing_count} con dati mancanti</span>
                {tipo === "anagrafiche" && <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400"><CopyCheck className="h-4 w-4" /> {validation.duplicate_count} già presenti</span>}
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-rose-100 dark:bg-rose-950/50 border border-rose-300" /> Dati obbligatori mancanti</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-100 dark:bg-amber-950/50 border border-amber-300" /> Cliente già presente (duplicato)</span>
              </div>
            </Card>
          )}

          <Card className="p-0 overflow-hidden">
            <div className="px-5 py-3 border-b border-border flex flex-wrap items-center gap-3">
              <FileSpreadsheet className="h-4 w-4 text-primary" />
              <h2 className="font-heading font-semibold text-sm">Anteprima importazione — tutte le {preview.total} righe</h2>
              <div className="ml-auto inline-flex rounded-lg border border-border p-0.5 bg-muted/40">
                <button data-testid="import-view-mapped" onClick={() => setViewMode("mapped")} className={`px-3 py-1 text-xs rounded-md transition-colors ${viewMode === "mapped" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}>Come verrà importato</button>
                <button data-testid="import-view-file" onClick={() => setViewMode("file")} className={`px-3 py-1 text-xs rounded-md transition-colors ${viewMode === "file" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}>File originale</button>
              </div>
            </div>
            <div className="overflow-auto max-h-[65vh]">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 border-b border-border sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold whitespace-nowrap sticky left-0 bg-muted/50 z-20">#</th>
                    {viewMode === "file"
                      ? preview.columns.map((c) => <th key={c} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{c}</th>)
                      : FIELDS[tipo].map((f) => <th key={f.key} className={`px-3 py-2 text-left font-semibold whitespace-nowrap ${mapping[f.key] ? "" : "text-muted-foreground/50"}`} title={f.label}>{shortLabel(f)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => {
                    const fl = flags[i] || {};
                    const cls = fl.missing && fl.missing.length ? "bg-rose-50 dark:bg-rose-950/30" : fl.duplicate ? "bg-amber-50 dark:bg-amber-950/30" : "";
                    return (
                      <tr key={i} data-testid={`import-row-${i}`} className={`border-b border-border last:border-0 ${cls}`}>
                        <td className={`px-3 py-2 text-muted-foreground whitespace-nowrap sticky left-0 z-10 ${cls || "bg-background"}`}>{i + 1}</td>
                        {viewMode === "file"
                          ? preview.columns.map((c) => <td key={c} className="px-3 py-2 whitespace-nowrap max-w-[220px] truncate" title={String(r[c])}>{String(r[c])}</td>)
                          : FIELDS[tipo].map((f) => { const v = mval(r, f.key); return <td key={f.key} className={`px-3 py-2 whitespace-nowrap max-w-[220px] truncate ${mapping[f.key] ? "" : "text-muted-foreground/40"}`} title={v}>{v}</td>; })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {progress && (
            <Card className="p-4 space-y-2" data-testid="import-progress">
              <div className="flex justify-between text-sm"><span className="font-medium">Importazione in corso…</span><span className="tabular-nums" data-testid="import-progress-pct">{progress.pct}% · {progress.done}/{progress.total}</span></div>
              <Progress value={progress.pct} />
            </Card>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={() => { setPreview(null); setMapping({}); }}>Annulla</Button>
            <Button data-testid="import-confirm-btn" onClick={commit} disabled={busy}>
              <CheckCircle2 className="h-4 w-4 mr-2" /> Conferma e importa {preview.total} righe
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
